import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { TimeOffForm } from '../components/TimeOffForm';
import { useCreateTimeOff, useTimeOffItem, useUpdateTimeOff } from '../queries';
import type { TimeOffInput } from '../types';

export function TimeOffFormPage() {
  const { publicId = '' } = useParams();
  const isEdit = Boolean(publicId);
  const navigate = useNavigate();

  const requestQuery = useTimeOffItem(isEdit ? publicId : -1);
  const create = useCreateTimeOff();
  const update = useUpdateTimeOff(publicId);

  async function handleSubmit(values: TimeOffInput) {
    const saved = isEdit ? await update.mutateAsync(values) : await create.mutateAsync(values);
    navigate(`/hrm/time-off/${saved.public_id}`);
  }

  if (isEdit && requestQuery.isPending) {
    return (
      <div className="w-full max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const request = requestQuery.data;

  return (
    <div className="w-full max-w-3xl space-y-8">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(isEdit ? `/hrm/time-off/${publicId}` : '/hrm/time-off')}
        >
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? 'Edit time-off request' : 'New time-off request'}
        </h1>
      </div>

      <TimeOffForm
        submitLabel={isEdit ? 'Save changes' : 'File request'}
        defaultValues={
          request
            ? {
                employee: String(request.employee),
                type: request.type,
                start_date: request.start_date,
                end_date: request.end_date,
                days: request.days,
                reason: request.reason,
              }
            : undefined
        }
        onSubmit={handleSubmit}
        onCancel={() => navigate(isEdit ? `/hrm/time-off/${publicId}` : '/hrm/time-off')}
      />
    </div>
  );
}
